import { InternalZap, Session } from "../../api/types";
import { assertNeverSoftly } from "../../lib/assertNever";
import { Logger } from "../../lib/logger";
import { rootElementMetadata } from "../../lib/root-element-metadata";
import * as WFE from "../../lib/manage-zaps";

/** Waiting for props */
export interface StatePending {
  type: "pending";
}

/** User in interacting with the log in pop-up */
interface StateSigningIn {
  type: "signing-in";
  props: WFE.Props;
}

/** Checking signed-in state */
export interface StateCheckingSession {
  type: "checking-session";
  props: WFE.Props;
}

/** Loading Zap data */
interface StateLoading {
  type: "loading";
  props: WFE.Props;
}

/** Error state when we don't know how to recover */
interface StateErrored {
  type: "errored";
  props: WFE.Props;
  error: Error;
}

/** User is not signed in */
interface StateSignedOut {
  type: "signed-out";
  props: WFE.Props;
}

/** User is signed in */
interface StateSignedIn {
  type: "signed-in";
  props: WFE.Props;
  session: Session;
}

/** Auth flow succeeded and data is loaded */
interface StateLoaded {
  type: "loaded";
  props: WFE.Props;
  session: Session;
  zaps: InternalZap[];
  appSlug: string;
  appSelectedAPI: string;
  page: number;
  count: number;
  currentURL: string;
  nextURL: string | null;
  previousURL: string | null;
}

interface StateLoadingPage extends Omit<StateLoaded, "type"> {
  type: "loading-paginated-zaps";
}

interface StateRefreshing extends Omit<StateLoaded, "type"> {
  type: "refreshing";
}

interface StateNavigating {
  type: "navigating";
  destinationURL: string;
}

// Tagged unions pattern
// https://www.wavebeem.com/blog/2020/10/21/tagged-unions-in-javascript/
export type ManageZapsIframeState =
  | StatePending
  | StateSigningIn
  | StateCheckingSession
  | StateLoading
  | StateSignedIn
  | StateErrored
  | StateSignedOut
  | StateLoaded
  | StateLoadingPage
  | StateNavigating
  | StateRefreshing;

// Check if the root element metadata from outside the iframe matches the
// metadata on the inside. This is used to detect version mismatches between the
// inner and outer scripts, which could result in bugs.
export function checkMetadata(props: WFE.Props) {
  try {
    type MetaObj = typeof rootElementMetadata;
    type MetaKey = keyof MetaObj;
    const keys = Object.keys(props.rootElementMetadata) as MetaKey[];
    for (const key of keys) {
      const inner = rootElementMetadata[key];
      const outer = props.rootElementMetadata[key];
      if (inner !== outer) {
        Logger.error(`Metadata "${key}" mismatch:`, inner, "!==", outer);
      }
    }
  } catch {
    Logger.error("Failed to check metadata");
  }
}

export function reducer(
  state: ManageZapsIframeState,
  action: WFE.Action,
): ManageZapsIframeState {
  switch (action.type) {
    case "zapier-elements.workflow.action.refresh":
    case "zapier-elements.workflow.action.close":
    case "zapier.popup.close": {
      if ("props" in state) {
        return { type: "checking-session", props: state.props };
      } else {
        Logger.warn(
          "close action received during incorrect state",
          action,
          state,
        );
        return state;
      }
    }
    case "zapier-elements.workflow.action.update": {
      // If we're in a state that already has props, check to see if we can
      // avoid starting the entire "checking-session" state all over again,
      // instead just updating the props.
      if ("props" in state) {
        checkMetadata(state.props);
        if (state.props.clientID !== action.props.clientID) {
          return { type: "checking-session", props: action.props };
        } else {
          return {
            ...state,
            props: {
              ...state.props,
              ...action.props,
            },
          };
        }
      } else {
        return { type: "checking-session", props: action.props };
      }
    }
    default: {
      // Exhaustiveness check
      // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#exhaustiveness-checking
      assertNeverSoftly(action);
      return state;
    }
  }
}
